import type { CommuteDestination } from './types';

export const MAX_AUTO_COMMUTE_DISTANCE_MILES = 100;

type Coordinate = {
  lat: number;
  lng: number;
};

export type CommuteDistanceStatus = {
  distanceMiles: number;
  isWithinAutoLimit: boolean;
};

const EARTH_RADIUS_MILES = 3958.8;
const DISTANCE_LIMIT_EPSILON_MILES = 0.000001;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function getDistanceMiles(origin: Coordinate, destination: Coordinate): number {
  const dLat = toRadians(destination.lat - origin.lat);
  const dLng = toRadians(destination.lng - origin.lng);
  const originLat = toRadians(origin.lat);
  const destinationLat = toRadians(destination.lat);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(originLat) * Math.cos(destinationLat) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
}

export function getCommuteDistanceStatus(origin: Coordinate, destination: CommuteDestination): CommuteDistanceStatus {
  const distanceMiles = getDistanceMiles(origin, { lat: destination.lat, lng: destination.lng });
  return {
    distanceMiles,
    isWithinAutoLimit: distanceMiles <= MAX_AUTO_COMMUTE_DISTANCE_MILES + DISTANCE_LIMIT_EPSILON_MILES,
  };
}

export function getCommuteDestinationKey(destination: CommuteDestination): string {
  return `${destination.placeId}:${destination.lat}:${destination.lng}`;
}

export function formatCommuteDistanceMiles(distanceMiles: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(distanceMiles);
}
